<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：パスワード変更
 *
 * 作成者：2021/11/18 ノア）虞
 * 最終修正者：2022/1/10 ノア）黄
 *
 -->
<template>
  <div>
    <!-- パスワード変更 -->
    <ttiDialog
      :isShow="isShow"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('updatePassword.title')"
      width="600px"
    >
      <template slot="body">
        <el-form
          :model="updatePasswordForm"
          :rules="passwordRules"
          ref="updatePasswordFormRef"
          label-width="220px"
          class="update-pass"
          size="mini"
        >
          <el-form-item :label="$t('updatePassword.oldPassword')" prop="password">
            <el-input
              size="small"
              type="password"
              v-model.trim="updatePasswordForm.password"
              autocomplete="off"
            ></el-input>
          </el-form-item>
          <el-form-item :label="$t('updatePassword.newPassword')" prop="newPassword">
            <el-input
              size="small"
              type="password"
              v-model.trim="updatePasswordForm.newPassword"
              autocomplete="off"
            ></el-input>
          </el-form-item>
          <el-form-item :label="$t('updatePassword.checkPassword')" prop="checkPass">
            <el-input
              size="small"
              type="password"
              v-model.trim="updatePasswordForm.checkPass"
              autocomplete="off"
            ></el-input>
          </el-form-item>
        </el-form>
      </template>
      <template slot="foot">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          v-tooltip="{
            content: `${$t('master.tooltipCommon.save')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { i18n } from '@/locales'

import { UpdatePasswordParam } from '@/models'
import { dialog } from '@/store/modules'
import { postUpdatePassword } from '@/store/apis'
import { VALID_PATTERN, RULES, validateService } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import TtiDialog from '@/components/TtiDialog.vue'
import manualConfig from '@/config/manualConfig'

@Component({
  name: 'updatePassword',
  components: { TtiDialog }
})
export default class UpdatePassword extends Vue {
  /**
   * ログインフォームのref
   */
  @Ref() readonly updatePasswordFormRef!: Form

  /**
   * ダイアログボックスを表示するかどうかを取得する（画面用）
   *
   * @returns {boolean} ダイアログボックスを表示するかどうか（画面用）
   */
  get isShow(): boolean | undefined {
    return dialog.isUpdatePsDialogShow
  }

  /**
   * ダイアログボックスを表示するかどうかを設定する（画面用）
   */
  set isShow(value: boolean | undefined) {
    this.handelClose()
  }

  /**
   * 表示状態変更
   *
   * @returns {void}
   */
  onHandelDisplay(): void {
    this.handelClose()
  }

  /**
   * パスワード変更サブ画面のマニュアルキーを取得
   *
   * @returns {string} パスワード変更サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.updatePassword
  }

  /**
   * パスワード情報
   */
  updatePasswordForm: UpdatePasswordParam = {
    password: '',
    newPassword: '',
    checkPass: ''
  }

  /**
   * パスワードルール
   */
  passwordRules: Form['rules'] = {
    password: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('updatePassword.oldPassword')]).toString(),
        trigger: 'blur'
      },
      // パスワード
      {
        pattern: VALID_PATTERN[RULES.PASSWORD],
        message: i18n.t('vaildate.common.password', [i18n.t('updatePassword.oldPassword')]).toString(),
        trigger: 'blur'
      },
      // 8~20文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.RANGE,
            range: [8, 20],
            message: i18n.t('vaildate.common.range', [i18n.t('updatePassword.oldPassword'), 8, 20]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    newPassword: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('updatePassword.newPassword')]).toString(),
        trigger: 'blur'
      },
      // パスワード
      {
        pattern: VALID_PATTERN[RULES.PASSWORD],
        message: i18n.t('vaildate.common.password', [i18n.t('updatePassword.newPassword')]).toString(),
        trigger: 'blur'
      },
      // 8~20文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.RANGE,
            range: [8, 20],
            message: i18n.t('vaildate.common.range', [i18n.t('updatePassword.newPassword'), 8, 20]).toString()
          },
          // 複雑さチェック
          {
            rule: RULES.MUTIPLE,
            callback: (value: string): string | undefined => {
              if (validateService.passwordComplexityValidate(value)) {
                return
              }

              const message = i18n.t('vaildate.common.complexity').toString()
              return message
            }
          }
        ]),
        trigger: 'blur'
      }
    ],
    checkPass: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('updatePassword.checkPassword')]).toString(),
        trigger: 'blur'
      },
      // パスワード
      {
        pattern: VALID_PATTERN[RULES.PASSWORD],
        message: i18n.t('vaildate.common.password', [i18n.t('updatePassword.checkPassword')]).toString(),
        trigger: 'blur'
      },
      // 8~20文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.RANGE,
            range: [8, 20],
            message: i18n.t('vaildate.common.range', [i18n.t('updatePassword.checkPassword'), 8, 20]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    if (this.updatePasswordForm.password === this.updatePasswordForm.newPassword) {
      msgBoxService.info(i18n.t('vaildate.logout.E00071').toString())
      return false
    }

    if (this.updatePasswordForm.newPassword !== this.updatePasswordForm.checkPass) {
      msgBoxService.info(i18n.t('vaildate.logout.E00070').toString())
      return false
    }

    return true
  }

  /**
   * パスワード変更確認ボタンをクリックする
   *
   * @returns {void}
   */
  onSaveButtonClick(): void {
    this.updatePasswordFormRef.validate(valid => {
      if (!valid) {
        return
      }

      if (!this.correlationCheck()) {
        return
      }

      const params = {
        password: window.btoa(this.updatePasswordForm.password),
        newPassword: window.btoa(this.updatePasswordForm.newPassword)
      }

      this.doUpdatePassword(params)
    })
  }

  /**
   * 操作ポップアップボックスが表示されている、または表示されていない
   *
   * @returns {void}
   */
  handelClose(): void {
    dialog.setUpdatePsIsShow()
    this.updatePasswordForm = {
      password: '',
      newPassword: '',
      checkPass: ''
    }
    this.updatePasswordFormRef.clearValidate()
  }
  /**
   * パスワードを変更する
   *
   * @param {UpdatePasswordParam} updatePasswordParam ログインのパラメータ
   * @returns {void}
   */
  async doUpdatePassword(updatePasswordParam: UpdatePasswordParam): Promise<void> {
    try {
      await postUpdatePassword(updatePasswordParam)

      this.doWhenUpdateSuccess()
    } catch (e) {
      Vue.$log.debug('Login > doUpdatePassword: e=', e)
      throw e
    }
  }

  /**
   * パスワードを変更した時の処理
   *
   * @returns {void}
   */
  doWhenUpdateSuccess(): void {
    const confirmCallback = () => {
      this.handelClose()
    }

    msgBoxService.info(i18n.t('messages.common.updateSuccess').toString(), confirmCallback)
  }
}
</script>

<style scoped lang="scss">
.update-pass .el-input {
  width: 75%;
}
</style>
